import { ApiError } from '@signalvote/api-client';
import { Download, ImageIcon } from 'lucide-react';
import { PendingButton } from '@/components/ui';
import { useTextSendResultsDownload, useTextSendStatus } from '@/hooks/queries';
import { authService } from '@/services/authService';
import { PUBLIC_LINK_HEADLINE as HEADLINE, PublicLinkPage as Page } from './PublicLinkPage';
import { SendChecklist, progressOfStatusLink } from './SendChecklist';
import { describeSendTime, timeZoneName } from './workOrderSchedule';

/**
 * A Text send's status link: open to whoever holds it, no sign-in. Read-only; anything that changes
 * the send links to sign in first.
 */
export function TextSendStatusPage({ token }: { token: string }) {
  const { data, error, isLoading } = useTextSendStatus(token);
  const results = useTextSendResultsDownload(token);
  const { mutate: downloadResults } = results;

  if (isLoading) {
    return (
      <Page>
        <p role="status" className="text-sm text-muted-foreground">
          Loading your text…
        </p>
      </Page>
    );
  }
  if (!data) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <Page>
        <h1 className={HEADLINE}>{notFound ? 'We couldn’t find this text.' : 'We couldn’t load this page.'}</h1>
        <p className="mt-3 text-[1.0625rem] text-muted-foreground">
          {notFound ? 'Check that the link matches the one in your email.' : 'Refresh to try again.'}
        </p>
      </Page>
    );
  }

  const when = describeSendTime(data.sendDate, data.sendTime);
  const zone = timeZoneName(data.timeZoneId) ?? 'your campaign’s time';
  return (
    <Page>
      <p className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">{data.campaignName}</p>
      <h1 className={`${HEADLINE} mt-3`}>Your text</h1>
      <p className="mt-3 text-[1.0625rem] text-muted-foreground">
        {when ? `Goes out ${when}, ${zone}.` : 'No send time yet.'}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">Order {data.reference}</p>
      {data.hasResultsReport && (
        <div className="mt-6">
          <PendingButton
            pending={results.isPending}
            onClick={() => downloadResults(undefined, { onSuccess: (link) => window.location.assign(link.url) })}
          >
            <Download className="size-4" aria-hidden />
            Download your results
          </PendingButton>
          {results.error && (
            <p role="alert" className="mt-3 text-sm text-destructive">
              We couldn’t start the download. Try again.
            </p>
          )}
        </div>
      )}

      <div className="mt-10 grid gap-10 md:grid-cols-[1fr_260px]">
        <SendChecklist progress={progressOfStatusLink(data)} signedIn={authService.isAuthenticated()} />
        {data.message && (
          <section aria-label="Your message" className="space-y-2">
            <div
              data-testid="status-message"
              className="whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-muted px-4 py-3 text-sm text-foreground [overflow-wrap:anywhere]"
            >
              {data.message}
            </div>
            {data.hasImage && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <ImageIcon className="h-3.5 w-3.5" aria-hidden="true" />
                With your image
              </p>
            )}
          </section>
        )}
      </div>
    </Page>
  );
}
